import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, Calculator, ChevronDown } from "lucide-react";
import { useSettings, SectionHeader, SaveBar } from "./shared";
import SeasonCalendar from "./SeasonCalendar";

const SEASON_COLORS = ["sky", "amber", "rose", "emerald", "violet", "slate"];
const COLOR_CHIP = {
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  violet: "bg-violet-100 text-violet-700 border-violet-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

export function seasonForDate(seasons, isoDate) {
  if (!isoDate) return null;
  const md = isoDate.slice(5);
  return (seasons || []).find((s) => {
    if (!s.start || !s.end) return false;
    return s.start <= s.end ? md >= s.start && md <= s.end : md >= s.start || md <= s.end;
  }) || null;
}

export function tierForDays(tiers, days) {
  return (tiers || []).find((t) => days >= (t.min_days || 1) && (t.max_days == null || t.max_days === "" || days <= t.max_days)) || null;
}

export function tierLabel(t) {
  if (t.max_days == null || t.max_days === "") return `${t.min_days} jour${t.min_days > 1 ? "s" : ""} et +`;
  if (t.min_days === t.max_days) return `${t.min_days} jour${t.min_days > 1 ? "s" : ""}`;
  return `${t.min_days} à ${t.max_days} jours`;
}

function SeasonRow({ s, i, update, remove, testid }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 border-b last:border-b-0 border-slate-100">
      <div className="relative shrink-0">
        <button type="button" onClick={() => setPaletteOpen((o) => !o)} aria-label="Changer la couleur" className={`h-7 w-7 rounded-full border-2 ${COLOR_CHIP[s.color] || COLOR_CHIP.slate}`} data-testid={`${testid}-palette-${i}`} />
        {paletteOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setPaletteOpen(false)} />
            <div className="absolute z-20 top-9 left-0 flex gap-1.5 p-2 rounded-xl border border-slate-200 bg-white shadow-lg">
              {SEASON_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => { update(i, { color: c }); setPaletteOpen(false); }} className={`h-6 w-6 rounded-full border-2 ${COLOR_CHIP[c]} ${s.color === c ? "ring-2 ring-offset-1 ring-slate-400" : ""}`} data-testid={`${testid}-color-${i}-${c}`} />
              ))}
            </div>
          </>
        )}
      </div>
      <input value={s.label} onChange={(e) => update(i, { label: e.target.value })} className="flex-1 min-w-0 h-9 px-2.5 rounded-lg border border-slate-200 text-sm font-medium focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" data-testid={`${testid}-label-${i}`} />
      <button type="button" onClick={() => remove(i)} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" data-testid={`${testid}-remove-${i}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export function SeasonsEditor({ seasons, onChange, testid = "seasons-editor" }) {
  const update = (i, patch) => onChange(seasons.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const add = () => onChange([...seasons, { id: `s-${Date.now()}`, label: "Nouvelle saison", color: SEASON_COLORS[seasons.length % SEASON_COLORS.length] }]);
  const remove = (i) => onChange(seasons.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
        {seasons.map((s, i) => (
          <SeasonRow key={s.id} s={s} i={i} update={update} remove={remove} testid={testid} />
        ))}
      </div>
      <button type="button" onClick={add} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900 transition-colors" data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5" /> Ajouter une saison
      </button>
      <div className="text-[11px] text-slate-400 mt-1">Une saison définit une configuration tarifaire (nom + couleur). Ses périodes se planifient dans le calendrier saisonnier ci-dessous. Les saisons non utilisées restent disponibles.</div>
    </div>
  );
}

export function TiersEditor({ tiers, onChange, testid = "tiers-editor" }) {
  const update = (i, patch) => {
    const next = tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    onChange(next.map((t) => ({ ...t, label: tierLabel(t) })));
  };
  const add = () => {
    const last = tiers[tiers.length - 1];
    const min = last ? (last.max_days ? last.max_days + 1 : (last.min_days || 1) + 7) : 1;
    const t = { id: `t-${Date.now()}`, min_days: min, max_days: null };
    onChange([...tiers, { ...t, label: tierLabel(t) }]);
  };
  const remove = (i) => onChange(tiers.filter((_, idx) => idx !== i));
  return (
    <div data-testid={testid}>
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
        {tiers.map((t, i) => (
          <div key={t.id} className="flex items-center gap-2 px-3 py-2.5 border-b last:border-b-0 border-slate-100">
            <span className="text-[10px] text-slate-400 font-semibold uppercase w-6">de</span>
            <input type="number" min="1" value={t.min_days ?? ""} onChange={(e) => update(i, { min_days: e.target.value === "" ? 1 : Number(e.target.value) })} className="w-16 h-8 px-2 rounded-md border border-slate-200 text-xs text-center" data-testid={`${testid}-min-${i}`} />
            <span className="text-[10px] text-slate-400 font-semibold uppercase">à</span>
            <input type="number" min="1" value={t.max_days ?? ""} placeholder="∞" onChange={(e) => update(i, { max_days: e.target.value === "" ? null : Number(e.target.value) })} className="w-16 h-8 px-2 rounded-md border border-slate-200 text-xs text-center" data-testid={`${testid}-max-${i}`} />
            <span className="text-[10px] text-slate-400 font-semibold uppercase">jours</span>
            <span className="ml-auto text-xs font-medium text-slate-600">{tierLabel(t)}</span>
            <button type="button" onClick={() => remove(i)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`${testid}-remove-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900 transition-colors" data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5" /> Ajouter un palier
      </button>
      <div className="text-[11px] text-slate-400 mt-1">Laissez le maximum vide pour « et + ». Le tarif devient dégressif au fil des paliers.</div>
    </div>
  );
}

export function RateGrid({ categories, seasons, tiers, rates, onChange, testid = "rate-grid" }) {
  const [cat, setCat] = useState(categories[0]?.value || "");
  const setPrice = (seasonId, tierId, val) => {
    const catRates = rates[cat] || {};
    onChange({ ...rates, [cat]: { ...catRates, [seasonId]: { ...(catRates[seasonId] || {}), [tierId]: val === "" ? "" : Number(val) } } });
  };
  const copyFrom = (src) => {
    if (!src) return;
    onChange({ ...rates, [cat]: JSON.parse(JSON.stringify(rates[src] || {})) });
    toast.success(`Grille copiée depuis ${src}`);
  };
  const filledCats = categories.filter((c) => Object.values(rates[c.value] || {}).some((season) => Object.values(season || {}).some((p) => Number(p) > 0)));
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {categories.map((c) => {
          const filled = filledCats.some((f) => f.value === c.value);
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => setCat(c.value)}
              className={`inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold transition-colors ${cat === c.value ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-violet-400"}`}
              data-testid={`${testid}-cat-${c.value}`}
            >
              {c.label}
              {filled && <span className={`h-1.5 w-1.5 rounded-full ${cat === c.value ? "bg-emerald-400" : "bg-emerald-500"}`} />}
            </button>
          );
        })}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-400">Saison \ Durée</th>
              {tiers.map((t) => (
                <th key={t.id} className="px-3 py-2.5 text-[11px] font-bold text-slate-600 whitespace-nowrap">{tierLabel(t)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {seasons.map((s) => (
              <tr key={s.id} className="border-b last:border-b-0 border-slate-50">
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold ${COLOR_CHIP[s.color] || COLOR_CHIP.slate}`}>{s.label}</span>
                </td>
                {tiers.map((t) => {
                  const val = ((rates[cat] || {})[s.id] || {})[t.id];
                  return (
                    <td key={t.id} className="px-2 py-2 text-center">
                      <div className="relative inline-block">
                        <input
                          type="number"
                          min="0"
                          value={val ?? ""}
                          placeholder="—"
                          onChange={(e) => setPrice(s.id, t.id, e.target.value)}
                          className="w-24 h-9 pl-2 pr-9 rounded-lg border border-slate-200 text-sm text-right font-medium focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
                          data-testid={`rate-${cat}-${s.id}-${t.id}`}
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-semibold pointer-events-none">€/j</span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filledCats.length > 0 && filledCats.every((f) => f.value !== cat) && (
        <div className="mt-2 flex items-center gap-2">
          <Copy className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-xs text-slate-500">Dupliquer depuis :</span>
          {filledCats.map((f) => (
            <button key={f.value} type="button" onClick={() => copyFrom(f.value)} className="text-xs font-semibold text-violet-700 hover:underline" data-testid={`${testid}-copy-${f.value}`}>{f.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Simulator({ categories }) {
  const [cat, setCat] = useState(categories[0]?.value || "");
  const [dateVal, setDateVal] = useState(new Date().toISOString().slice(0, 10));
  const [days, setDays] = useState(5);
  const [method, setMethod] = useState("per_day");
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!cat || !dateVal) return;
    const t = setTimeout(() => {
      api.get("/vendor/pricing/resolve", { params: { category: cat, start_date: dateVal, days: Number(days) || 1, method } })
        .then((r) => setRes(r.data)).catch(() => setRes({ resolved: false }));
    }, 250);
    return () => clearTimeout(t);
  }, [cat, dateVal, days, method]);
  return (
    <div data-testid="pricing-simulator">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[10px] font-semibold text-slate-500 mb-1">Catégorie</div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs font-medium" data-testid="simulator-category">
            {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] font-semibold text-slate-500 mb-1">Date de départ</div>
          <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs" data-testid="simulator-date" />
        </div>
        <div>
          <div className="text-[10px] font-semibold text-slate-500 mb-1">Durée (jours)</div>
          <input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="w-20 h-9 px-2 rounded-lg border border-slate-200 bg-white text-xs text-center" data-testid="simulator-days" />
        </div>
        <div>
          <div className="text-[10px] font-semibold text-slate-500 mb-1">Méthode</div>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            <button type="button" onClick={() => setMethod("per_day")} className={`px-2.5 h-9 text-[11px] font-semibold ${method === "per_day" ? "bg-violet-600 text-white" : "bg-white text-slate-600"}`} data-testid="simulator-method-per_day">Jour par jour</button>
            <button type="button" onClick={() => setMethod("departure")} className={`px-2.5 h-9 text-[11px] font-semibold ${method === "departure" ? "bg-violet-600 text-white" : "bg-white text-slate-600"}`} data-testid="simulator-method-departure">Saison du départ</button>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-violet-100" data-testid="simulator-result">
        {res && res.resolved ? (
          <div className="text-sm space-y-1">
            {res.multi_season && <div className="text-[11px] font-bold text-violet-700 mb-1" data-testid="simulator-multi">Réservation sur plusieurs saisons :</div>}
            {(res.breakdown || []).map((b, i) => (
              <div key={i} className="flex justify-between text-xs text-slate-600"><span>{b.season_label} · {b.days} j × {b.daily_price} €</span><span className="font-semibold">{b.subtotal} €</span></div>
            ))}
            <div className="flex justify-between pt-1 border-t border-slate-100 text-sm font-bold text-slate-900"><span>Total ({res.tier?.label})</span><span data-testid="simulator-total">{res.total} €</span></div>
          </div>
        ) : (
          <div className="text-xs text-amber-700 font-medium" data-testid="simulator-unconfigured">Aucun tarif n'est configuré pour cette catégorie, cette saison et cette durée. Veuillez compléter la grille tarifaire.</div>
        )}
      </div>
    </div>
  );
}

function Accordion({ title, step, children, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-5 sm:px-6 py-4 text-left" data-testid={`${testid}-toggle`}>
        {step && <span className="h-6 w-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0">{step}</span>}
        <span className="text-sm font-semibold text-slate-800 flex-1">{title}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 sm:px-6 pb-5 sm:pb-6">{children}</div>}
    </div>
  );
}

export default function PricingSection() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setLocal(JSON.parse(JSON.stringify(settings.pricing))); }, [settings]);
  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  const categories = settings.fleet?.categories || [];
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.pricing);
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const calendar = local.season_calendar || { reference_season: local.seasons[0]?.id, recurring: [], exceptions: {} };
  const save = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { pricing: local }); toast.success("Grille tarifaire enregistrée"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  return (
    <div className="max-w-5xl pb-24">
      <SectionHeader overline="Tarification" title="Grille tarifaire" subtitle="Source unique des tarifs de location. Chaque bien est facturé selon sa catégorie, la saison et la durée — aucun prix n'est défini sur le véhicule." />

      <Accordion title="Gestion des saisons" step="1" defaultOpen testid="pricing-acc-seasons">
        <SeasonsEditor seasons={local.seasons} onChange={(v) => set("seasons", v)} />
      </Accordion>

      <Accordion title="Calendrier saisonnier" step="2" defaultOpen testid="pricing-acc-calendar">
        <SeasonCalendar seasons={local.seasons} calendar={calendar} onChange={(v) => set("season_calendar", v)} />
      </Accordion>

      <Accordion title="Tarifs dégressifs (paliers de durée)" step="3" testid="pricing-acc-tiers">
        <TiersEditor tiers={local.duration_tiers} onChange={(v) => set("duration_tiers", v)} />
      </Accordion>

      <Accordion title="Tarifs par catégorie (€/jour)" step="4" testid="pricing-acc-rates">
        <RateGrid categories={categories} seasons={local.seasons} tiers={local.duration_tiers} rates={local.rates} onChange={(v) => set("rates", v)} />
      </Accordion>

      <Accordion title="Simulateur tarifaire" step="5" testid="pricing-acc-simulator">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-wider font-bold text-slate-400"><Calculator className="h-3.5 w-3.5 text-violet-600" /> Vérifiez vos tarifs configurés</div>
        <Simulator categories={categories} />
      </Accordion>

      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="pricing-save" />
    </div>
  );
}
